import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { BaseComponent, NotificationSettingsService, SubscriptionItemType, SubscriptionType } from '@ridango/core';
import { ApplicationFacade, OpenTripPlannerFacade, ShapeType } from '@ridango/components';
import { BehaviorSubject, combineLatest } from 'rxjs';
import Env from '@ridango/core/env';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

@Component({
    selector: 'rwidget-edit-subscriptions',
    templateUrl: './edit-subscriptions.component.html',
    styleUrls: ['./edit-subscriptions.component.scss']
})
export class EditSubscriptionsComponent extends BaseComponent implements OnInit, OnDestroy {
    assetsPath$ = this.applicationFacade.assetsPath$;

    ShapeType = ShapeType;
    SubscriptionType = SubscriptionType;

    private subscriptionItemsSubject = new BehaviorSubject<SubscriptionItemType[]>([]);
    subscriptionItems$ = this.subscriptionItemsSubject.asObservable();

    private itemToDeleteSubject = new BehaviorSubject<SubscriptionItemType>(null);
    itemToDelete$ = this.itemToDeleteSubject.asObservable();
    isDeleteModalOpen$ = this.itemToDelete$.pipe(map((item) => !!item));

    constructor(
        private applicationFacade: ApplicationFacade,
        private notificationSettingsService: NotificationSettingsService,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        @Inject('ENV') public env: Env
    ) {
        super();
    }
    ngOnInit(): void {
        this.openTripPlannerFacade.getAllRoutes();
        this.openTripPlannerFacade.getAllStops();

        combineLatest([this.openTripPlannerFacade.routes$, this.openTripPlannerFacade.allFormattedStops$])
            .pipe(
                filter(([routes, stops]) => routes.length > 0 && stops.length > 0),
                take(1),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(() => {
                this.loadSubscriptions();
            });
    }

    loadSubscriptions(): void {
        this.notificationSettingsService
            .getDisturbanceSubscriptions()
            .pipe(
                switchMap((subscriptions) =>
                    combineLatest([
                        this.openTripPlannerFacade.routes$,
                        this.openTripPlannerFacade.allFormattedStops$
                    ]).pipe(
                        take(1),
                        map(([routes, stops]) => ({ subscriptions, routes, stops }))
                    )
                ),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(({ subscriptions, routes, stops }) => {
                const transformedData = this.mergeSubscriptionData(subscriptions, routes, stops);
                this.subscriptionItemsSubject.next(transformedData);
            });
    }

    private mergeSubscriptionData(subscriptions: any[], routes: any[], stops: any[]): SubscriptionItemType[] {
        return subscriptions.map((subscription) => {
            const isRoute = !!subscription.routeId;

            if (isRoute) {
                const route = routes.find((r) => r.id === subscription.routeId);

                return {
                    id: subscription.id,
                    type: SubscriptionType.Route,
                    name: route?.longName || subscription.routeId,
                    isSubscribed: subscription.active,
                    label: route?.shortName || '',
                    icon: {
                        color: route?.icon?.color ?? undefined,
                        shape: ShapeType.ROUNDED
                    },
                    originalData: subscription
                };
            } else {
                const stop = stops.find((s) => s.id === subscription.stopId);

                return {
                    id: subscription.id,
                    type: SubscriptionType.Stop,
                    name: stop?.name || subscription.stopId,
                    isSubscribed: subscription.active,
                    icon: {
                        color: undefined,
                        shape: ShapeType.ROUNDED
                    },
                    originalData: subscription
                };
            }
        });
    }

    handleBackClicked(): void {
        if (window.history.length > 1) {
            window.history.back();
        } else {
            window.location.href = '/';
        }
    }

    onToggleChange(event: MatSlideToggleChange, item: SubscriptionItemType) {
        const currentItems = this.subscriptionItemsSubject.value;
        const updatedItems = currentItems.map((i) => (i.id === item.id ? { ...i, isSubscribed: event.checked } : i));
        this.subscriptionItemsSubject.next(updatedItems);

        const updateData = {
            ...item.originalData,
            active: event.checked
        };

        this.notificationSettingsService.updateDisturbanceSubscription(item.id, updateData).subscribe({
            error: (error) => {
                console.error('Failed to update toggle:', error);
                this.subscriptionItemsSubject.next(currentItems);
            }
        });
    }

    openDeleteConfirmation(item: any) {
        this.itemToDeleteSubject.next(item);
    }

    closeDeleteConfirmation() {
        this.itemToDeleteSubject.next(null);
    }

    confirmDelete() {
        const itemToDelete = this.itemToDeleteSubject.value;

        if (!itemToDelete) {
            return;
        }

        this.notificationSettingsService.deleteDisturbanceSubscription(itemToDelete.id).subscribe({
            next: () => {
                const currentItems = this.subscriptionItemsSubject.value;
                const updatedItems = currentItems.filter((item) => item.id !== itemToDelete.id);
                this.subscriptionItemsSubject.next(updatedItems);
                this.closeDeleteConfirmation();
            },
            error: (error) => {
                console.error('Failed to delete subscription: ', error);
                this.closeDeleteConfirmation();
            }
        });
    }

    isMobile() {
        return window.innerWidth < 768;
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
